<template>
  <div
    ref="rootRef"
    @focus.capture="onFocus"
    @blur.capture="onBlur"
  >
    <DSPopover
      v-model="isOpen"
      inherit-activator-width
    >
      <template #activator="{ setRef }">
        <DSFormField
          :id="id"
          :label="props.label"
          :inline-edit="props.inlineEdit"
          :required="props.required"
          :disabled="props.disabled"
          :error-messages="dsFormField.errorMessages.value"
          :name="props.name"
          :tooltip="props.tooltip"
          :hidden-label="props.hiddenLabel"
        >
          <div
            :ref="el => setRef(el as HTMLElement)"
            data-cy="ds-select__trigger"
            :class="DS_SELECT_CLASSES.Root"
          >
            <DSBaseInput
              ref="baseInputRef"
              v-model="baseInputModelValue"
              v-bind="baseInputProps"
              @click="onInputClick"
              @keydown.tab="onTab"
              @keydown.backspace="onBackspace"
              @keydown.enter.prevent="onEnter"
              @keydown.arrow-down="onArrowDown"
              @keydown.arrow-up="onArrowUp"
              @keydown="onKeydown"
              @clear="clearSelection"
              @suffix:click="onSuffixClick"
            >
              <template #content>
                <DSSelectHiddenInputs
                  :name="props.name"
                  :selected-values="selectedValues"
                  :multiple="props.multiple"
                />

                <div
                  v-if="showSelectedTags"
                  :class="DS_SELECT_CLASSES.SelectedOptions"
                >
                  <DSTag
                    v-for="selectedOption in selectedOptions"
                    :key="selectedOption?.value || 'null'"
                    :text="selectedOption?.label || ''"
                    :disabled="props.disabled"
                    removable
                    @keydown.enter="props.inlineEdit && $event.stopPropagation()"
                    @remove="onRemoveOption(selectedOption?.value)"
                  />
                </div>
              </template>
            </DSBaseInput>
          </div>
        </DSFormField>
      </template>

      <template #content>
        <div ref="contentRef">
          <DSOptionList
            v-model="optionListModelValue"
            v-model:active="activeOptionId"
            :options="normalizedOptions"
            :multiple="props.multiple"
            :disabled="props.disabled"
            :loading="props.loading"
            :list-id="listId"
            :keyboard-navigation-enabled="false"
          >
            <template #suffix="slotProps">
              <slot
                name="suffix"
                v-bind="slotProps"
              />
            </template>
          </DSOptionList>
        </div>
      </template>
    </DSPopover>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import DSPopover from "@/src/design_system/primitives/popover/components/DSPopover.vue";
import DSOptionList from "@/src/design_system/form_elements/option_list/components/DSOptionList.vue";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import { crossesFocusBoundary, isNativeFocusEvent } from "@/src/design_system/form_elements/shared/utils/focusBoundary";
import type {
  DSSelectModelValue,
  DSSelectNormalizedProps,
  DSSelectProps,
} from "@/src/design_system/form_elements/select/types/props";
import DSTag from "@/src/design_system/data_display/tag/components/DSTag.vue";
import DSSelectHiddenInputs from "@/src/design_system/form_elements/select/components/DSSelectHiddenInputs.vue";
import { useSelect } from "@/src/design_system/form_elements/select/composables/useSelect";
import { useSelectNormalization } from "@/src/design_system/form_elements/select/composables/useSelectNormalization";
import { DS_SELECT_CLASSES } from "@/src/design_system/form_elements/select/constants/classes";
import { DSSelectEmits } from "@/src/design_system/form_elements/select/types/emits";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { VueComponentTemplateRef } from "@/src/shared/types/vueTemplateRef";
import { DEFAULT_ITEM_TITLE_KEY, DEFAULT_ITEM_VALUE_KEY } from "@/src/design_system/form_elements/select/constants";
import type { DSOptionValue } from "@/src/design_system/form_elements/shared/types/option";

const props = withDefaults(defineProps<DSSelectProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  placeholder: "",
  disabled: false,
  multiple: false,
  required: false,
  errorMessages: undefined,
  label: undefined,
  autocomplete: false,
  clearable: true,
  rules: undefined,
  tooltip: undefined,
  itemTitle: DEFAULT_ITEM_TITLE_KEY,
  itemValue: DEFAULT_ITEM_VALUE_KEY,
});

const emit = defineEmits<DSSelectEmits>();

const modelValue = defineModel<DSSelectModelValue>({
  default: null,
});

const baseInputRef: VueComponentTemplateRef<typeof DSBaseInput> = useTemplateRef("baseInputRef");
const rootRef = ref<HTMLElement>();
const contentRef = ref<HTMLElement>();

const dsFormField = useDSFormField<DSSelectModelValue>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: modelValue,
  rules: computed(() => props.rules),
});

const { id } = useDSFormFieldId({ props });

const { normalizedOptions, normalizedModelValue } = useSelectNormalization(props, modelValue);

const selectProps = computed<Readonly<DSSelectNormalizedProps>>(() => ({
  ...props,
  options: normalizedOptions.value,
}));

const {
  isOpen,
  selectedValues,
  selectedOptions,
  removeOption,
  showSelectedTags,
  optionListModelValue,
  baseInputModelValue,
  baseInputProps: selectBaseInputProps,
  onBackspace,
  onInputFocus,
  clearSelection,
  onInputBlur,
  onInputClick,
  onSuffixClick,
  onTab,
  onEnter,
  onKeydown,
  listId,
  activeOptionId,
  onArrowDown,
  onArrowUp,
} = useSelect(normalizedModelValue, emit, selectProps, baseInputRef);

const baseInputProps = computed(() => ({
  ...selectBaseInputProps.value,
  id: id.value,
  hasError: !!dsFormField.errorMessages.value.length,
}));

const onFocus = (event: Event) => {
  if (!isNativeFocusEvent(event)) return;
  if (event.target === baseInputRef.value?.getInputElement()) onInputFocus(event);
  if (!crossesFocusBoundary(event, rootRef.value)) return;
  emit("focus", event);
};

const onBlur = (event: Event) => {
  if (!crossesFocusBoundary(event, rootRef.value)) return;
  onInputBlur();
  emit("blur", event);
  isOpen.value = false;
};

const { focus } = useDSInput(baseInputRef);
const blur = () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) activeElement.blur();
};

watch(isOpen, open => {
  if (!open && contentRef.value?.contains(document.activeElement)) focus();
});

const onRemoveOption = (value?: DSOptionValue) => {
  removeOption(value);
  if (props.inlineEdit) {
    focus();
  }
};

defineExpose({
  focus,
  blur,
});
</script>
